import type { ReactNode } from 'react';
import { useContext } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';

import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { AgentAuthorityContext } from '@/lib/layout/AgentAuthorityContext';
import { ReadOnlyField } from '@/lib/pages/forms/components/ReadOnlyField';
import { formatDate } from '@/lib/utils/dates';

function DatePicker<
  T extends FieldValues,
  TTransformedValues extends FieldValues | undefined = T,
>({
  control,
  name,
  label,
  orientation = 'horizontal',
  readOnly,
  // placeholder,
  min,
  max,
  required = false,
  disabled,
  overwriteLabelWidth,
  overwriteInputWidth,
  stretch,
  wideLabel,
  labelClassName,
  type = 'date',
  nativeRequired = false,
  onChange,
}: {
  control: Control<T, unknown, TTransformedValues>;
  name: Path<T> | string;
  label?: ReactNode;
  orientation?: 'horizontal' | 'vertical';
  min?: string | Date;
  max?: string | Date;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  overwriteLabelWidth?: string;
  overwriteInputWidth?: string;
  stretch?: boolean;
  wideLabel?: boolean;
  labelClassName?: string;
  type?: 'date' | 'month';
  nativeRequired?: boolean;
  onChange?: () => void;
}) {
  const { responsive, editMode } = useContext(AgentAuthorityContext);
  const normalizeBoundary = (value?: string | Date) =>
    value instanceof Date ? value.toISOString().slice(0, 10) : value;

  return (
    <FormField
      control={control}
      name={name as Path<T>}
      render={({ field }) => {
        if (!editMode || readOnly) {
          return (
            <ReadOnlyField
              label={label}
              value={formatDate(field.value)}
              orientation={orientation}
              wideLabel={wideLabel}
              stretch={stretch}
              responsive={responsive}
              overwriteInputWidth={overwriteInputWidth}
              overwriteLabelWidth={overwriteLabelWidth}
            />
          );
        } else {
          return (
            <FormItem
              className={`flex ${orientation === 'horizontal' ? 'items-center gap-4' : 'w-full flex-col'} ${responsive ? 'max-md:w-full max-md:flex-col' : ''}`}
            >
              {label ? (
                <FormLabel
                  className={`${orientation === 'horizontal' ? `${overwriteLabelWidth ? overwriteLabelWidth : `${stretch ? `${wideLabel ? 'w-2/3' : 'w-1/3'}` : `${wideLabel ? 'w-2/5' : 'w-1/5'}`}`}` : 'mb-2'} font-semibold leading-[1.5rem] ${responsive ? 'max-md:w-full' : ''} ${labelClassName}`}
                >
                  {label}
                  {(required || nativeRequired) && !disabled ? (
                    <span className="text-red-400">*</span>
                  ) : null}
                </FormLabel>
              ) : null}
              <div
                className={`${orientation === 'horizontal' ? `${overwriteInputWidth ? overwriteInputWidth : `${stretch ? `${wideLabel ? 'w-1/3' : 'w-2/3'}` : `${wideLabel ? 'w-1/5' : 'w-2/5'}`}`}` : ''} ${responsive ? 'max-md:w-full' : ''} !mt-0`}
              >
                <div className="flex w-full items-center">
                  <Input
                    placeholder="Type here"
                    {...field}
                    value={field.value || ''}
                    disabled={readOnly || disabled}
                    // required={required} // uncomment to enable FE validation
                    required={nativeRequired}
                    type={type}
                    min={normalizeBoundary(min)}
                    max={normalizeBoundary(max)}
                    onChange={(e) => {
                      field.onChange(e); // Call RHF's onChange
                      onChange?.(); // Call custom onChange callback
                    }}
                  />
                </div>
                <FormMessage />
              </div>
            </FormItem>
          );
        }
      }}
    />
  );
}

export { DatePicker as DateInput };
